import { type MouseEvent, type PointerEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { type PlayableCharacter } from '../render/AxieCharacter.ts';
import { Btn, type Loadout, type RoomDef, type SimEvent } from '../sim/types.ts';
import { Hud } from './Hud.tsx';
import { Sanctum } from './Sanctum.tsx';
import { loadAssistMode, saveAssistMode } from './progress.ts';
import { useGame } from './useGame.ts';

/** How long the "Room cleared" overlay shows before auto-teleporting back to
 *  the Grove hub. Matches the brief's own "under three seconds, skippable"
 *  completion-screen intent - "Play again" still overrides it instantly. */
const AUTO_RETURN_MS = 1800;

/** How long the "No charge" toast stays up after a wasted fireball press.
 *  Brief pillar 5: a control must never silently do nothing - the fired
 *  shot itself gets a beam flash (`Renderer.ts`), and a refused press needs
 *  its own, equally immediate tell. */
const RANGED_EMPTY_TOAST_MS = 500;

/**
 * One playable room: viewport, HUD, and controls legend.
 *
 * Split out from App so useGame - a hook, and so unconditional - only runs
 * while a room is actually being played. This now covers the Grove hub too:
 * it is a real room like any other (see `buildGroveHub`), just one that
 * never reaches `outcome: 'cleared'` and whose only content is doors.
 *
 * App keys this by `room.id`, so every field here (including `reportedRef`
 * below) already starts fresh on every room change - nothing here needs to
 * reset itself when `room` changes.
 */
export function GameView({
  room,
  onCleared,
  onReturnToMap,
  onDoorEnter,
  brambleHornUnlocked = false,
  character,
}: {
  room: RoomDef;
  /** Fired once, the tick outcome first becomes 'cleared'. */
  onCleared: (roomId: string) => void;
  /** Also doubles as "auto-teleport back to the hub" after a room clears. */
  onReturnToMap: () => void;
  /** Fired when the player activates an unlocked door (the `E` key, not just
   *  walking into it - see `Btn.Activate`). Only the Grove hub ever authors
   *  any doors, so this is a no-op everywhere else. */
  onDoorEnter?: (targetRoomId: string) => void;
  /** Whether Room 5 has been cleared (brief §7). Drives the sim's loadout
   *  (unlocks the Tail's fireball at zero charges), what the Sanctum
   *  reports, and whether the controls legend below mentions the fireball
   *  key at all. */
  brambleHornUnlocked?: boolean;
  character: PlayableCharacter;
}) {
  const viewportRef = useRef<HTMLDivElement>(null);
  const [sanctumOpen, setSanctumOpen] = useState(false);
  const [assistEnabled, setAssistEnabled] = useState(loadAssistMode);
  const [failures, setFailures] = useState(0);
  // Counter, not a boolean: a second empty press while the toast is still up
  // must restart its own timeout rather than being swallowed by the first.
  const [rangedEmptyToken, setRangedEmptyToken] = useState(0);

  // Stable identity unless the unlock itself flips, so useGame's game-loop
  // effect (which reads this once per mount) never sees spurious churn from
  // an inline object literal recreated on every GameView render.
  const loadout: Loadout = useMemo(() => ({ brambleHorn: brambleHornUnlocked }), [brambleHornUnlocked]);

  // A locked door's feedback is the HUD's persistent `doorPrompt` (driven by
  // `sim.state.doorPrompt`, live for as long as the player is in range) -
  // no separate toast needed here, `door:locked` only matters for the entry
  // that actually gets activated.
  const handleEvents = useCallback(
    (events: readonly SimEvent[]) => {
      for (const e of events) {
        if (e.type === 'door:entered') onDoorEnter?.(e.targetRoomId);
        if (e.type === 'ranged:empty') setRangedEmptyToken((t) => t + 1);
      }
    },
    [onDoorEnter],
  );

  useEffect(() => {
    if (rangedEmptyToken === 0) return;
    const t = setTimeout(() => setRangedEmptyToken(0), RANGED_EMPTY_TOAST_MS);
    return () => clearTimeout(t);
  }, [rangedEmptyToken]);

  const { hud, restart, setTouchButton } = useGame(
    viewportRef,
    room,
    character,
    handleEvents,
    loadout,
    assistEnabled,
  );
  const reportedRef = useRef(false);
  const failedRef = useRef(false);

  const toggleAssist = useCallback(() => {
    setAssistEnabled((enabled) => {
      const next = !enabled;
      saveAssistMode(next);
      return next;
    });
  }, []);

  useEffect(() => {
    if (hud.outcome === 'failed' && !failedRef.current) {
      failedRef.current = true;
      setFailures((count) => count + 1);
    }
    if (hud.outcome !== 'failed') failedRef.current = false;
  }, [hud.outcome]);

  // Mirrored into refs (updated in an effect, never during render - React
  // Compiler flags a direct `ref.current = x` in the render body) so the
  // cleared-outcome effect below can depend on stable primitives only.
  // App re-rendering for an unrelated reason (e.g. `handleCleared` itself
  // updating `cleared` state) must not hand that effect a new function
  // identity and restart it - restarting would cancel and never reschedule
  // the auto-return timeout, since the `reportedRef` guard would then block
  // it from being armed a second time.
  const onClearedRef = useRef(onCleared);
  const onReturnToMapRef = useRef(onReturnToMap);
  useEffect(() => {
    onClearedRef.current = onCleared;
    onReturnToMapRef.current = onReturnToMap;
  }, [onCleared, onReturnToMap]);

  useEffect(() => {
    if (hud.outcome !== 'cleared' || reportedRef.current) return;
    reportedRef.current = true;
    onClearedRef.current(room.id);
    const t = setTimeout(() => onReturnToMapRef.current(), AUTO_RETURN_MS);
    return () => clearTimeout(t);
  }, [hud.outcome, room.id]);

  return (
    <main className="app">
      <div className="stage">
        <div className="viewport" ref={viewportRef} />
        <Hud
          hud={hud}
          onRestart={restart}
          onReturnToMap={onReturnToMap}
          onOpenSanctum={() => setSanctumOpen(true)}
          assistEnabled={assistEnabled}
          showAssistOffer={failures >= 3 && !assistEnabled}
          onToggleAssist={toggleAssist}
        />
        {sanctumOpen && (
          <Sanctum brambleHornUnlocked={brambleHornUnlocked} onClose={() => setSanctumOpen(false)} />
        )}
        {rangedEmptyToken > 0 && <p className="ranged-empty">No charge</p>}
      </div>
      <TouchControls onButton={setTouchButton} brambleHornUnlocked={brambleHornUnlocked} />
      <p className="controls">
        <kbd>&larr;</kbd> <kbd>&rarr;</kbd> move &middot; <kbd>W</kbd> jump &middot; <kbd>Space</kbd>{' '}
        cast &middot; <kbd>S</kbd>+<kbd>Space</kbd> cast down &middot; <kbd>E</kbd> enter portal / activate door
        {brambleHornUnlocked && (
          <>
            {' '}
            &middot; <kbd>F</kbd> fireball{' '}
          </>
        )}
        &middot; hold <kbd>R</kbd> restart &middot; <kbd>Esc</kbd> pause
      </p>
    </main>
  );
}

/** Touch-only controls live below the playfield so thumbs never obscure a
 * jump or a cast target. Pointer capture keeps a held direction alive when a
 * finger drifts outside its button. */
function TouchControls({
  onButton,
  brambleHornUnlocked,
}: {
  onButton: (button: number, held: boolean) => void;
  brambleHornUnlocked: boolean;
}) {
  const buttonProps = (button: number) => ({
    onPointerDown: (event: PointerEvent<HTMLButtonElement>) => {
      event.preventDefault();
      event.currentTarget.setPointerCapture(event.pointerId);
      onButton(button, true);
    },
    onPointerUp: (event: PointerEvent<HTMLButtonElement>) => {
      onButton(button, false);
      event.currentTarget.releasePointerCapture(event.pointerId);
    },
    onPointerCancel: () => onButton(button, false),
    onContextMenu: (event: MouseEvent<HTMLButtonElement>) => event.preventDefault(),
  });

  return (
    <div className="touch-controls" aria-label="Touch controls">
      <div className="touch-move" aria-label="Move">
        <button type="button" className="touch-btn touch-direction" aria-label="Move left" {...buttonProps(Btn.Left)}>
          ←
        </button>
        <button type="button" className="touch-btn touch-direction" aria-label="Move right" {...buttonProps(Btn.Right)}>
          →
        </button>
      </div>
      <div className="touch-actions">
        <button type="button" className="touch-btn" {...buttonProps(Btn.Jump)}>Jump</button>
        <button type="button" className="touch-btn" {...buttonProps(Btn.Cast)}>Cast</button>
        <button type="button" className="touch-btn touch-low-cast" {...buttonProps(Btn.Down | Btn.Cast)}>Cast low</button>
        <button type="button" className="touch-btn" {...buttonProps(Btn.Activate)}>Enter</button>
        {brambleHornUnlocked && (
          <button type="button" className="touch-btn" {...buttonProps(Btn.Ranged)}>Fire</button>
        )}
      </div>
    </div>
  );
}
